import React, { useState } from 'react';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';

// Frozen interface — see docs/billing-contract.md §4. Parent must render this
// inside <Elements stripe={stripePromise}>; it does not create its own provider.

const cardElementOptions = (showPostalCode) => ({
  hidePostalCode: !showPostalCode,
  style: {
    base: {
      fontSize: '16px',
      color: '#32325d',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      '::placeholder': { color: '#aab7c4' },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
});

function CardEntry({
  clientSecret,
  mode,
  submitLabel,
  disabled = false,
  billingDetails,
  showPostalCode = true,
  onSuccess,
  onError,
  onProcessingChange,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [cardValidationError, setCardValidationError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const setProcessing = (value) => {
    setIsProcessing(value);
    if (onProcessingChange) onProcessingChange(value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      onError({ message: 'Payment system is still loading. Please try again in a moment.' });
      return;
    }

    const card = elements.getElement(CardElement);
    if (!card) {
      onError({ message: 'Card details are required.' });
      return;
    }

    setProcessing(true);

    try {
      const confirmFn = mode === 'setup'
        ? stripe.confirmCardSetup.bind(stripe)
        : stripe.confirmCardPayment.bind(stripe);

      const { error, paymentIntent, setupIntent } = await confirmFn(clientSecret, {
        payment_method: { card, billing_details: billingDetails },
      });

      if (error) {
        onError({
          message: error.message || 'Payment failed. Please try again.',
          code: error.code,
          declineCode: error.decline_code,
          type: error.type,
        });
        return;
      }

      const intent = mode === 'setup' ? setupIntent : paymentIntent;
      const paymentMethodId = typeof intent?.payment_method === 'string'
        ? intent.payment_method
        : intent?.payment_method?.id || null;

      onSuccess({
        status: intent?.status,
        paymentMethodId,
        mode,
        ...(mode === 'setup' ? { setupIntent: intent } : { paymentIntent: intent }),
      });
    } catch (err) {
      onError({ message: err.message || 'Payment failed. Please try again.' });
    } finally {
      setProcessing(false);
    }
  };

  const isDisabled = disabled || isProcessing || !stripe;

  return (
    <form onSubmit={handleSubmit} className="card-entry-form space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">Card Information</label>
        <div
          className="mt-1 p-4 border rounded-md bg-gray-50"
          style={{ minHeight: '48px', opacity: isDisabled ? 0.6 : 1 }}
        >
          <CardElement
            options={cardElementOptions(showPostalCode)}
            onChange={(event) => setCardValidationError(event.error ? event.error.message : null)}
          />
        </div>
        {cardValidationError && (
          <div className="mt-2 text-sm text-red-600" role="alert">
            {cardValidationError}
          </div>
        )}
      </div>

      <button
        type="submit"
        disabled={isDisabled}
        className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
      >
        {isProcessing ? 'Processing...' : (submitLabel || (mode === 'payment' ? 'Pay' : 'Save card'))}
      </button>
    </form>
  );
}

export { CardEntry };
export default CardEntry;
